import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiClient } from '@/services/api/apiClient';
import {
  CreateEventRequest,
  UpdateEventRequest,
  EventListParams,
  EventResponse,
  EventListResponse,
  EnrolledUsersResponse,
} from '@/constants/types';
import { API_ENDPOINTS } from '@/constants/endpoints';

export const eventService = {
  async getEvents(params?: EventListParams): Promise<EventListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (params?.eventMode) searchParams.append('eventMode', params.eventMode);
    if (params?.ticketType) searchParams.append('ticketType', params.ticketType);
    if (params?.eventVisibility) searchParams.append('eventVisibility', params.eventVisibility);
    if (params?.upcoming !== undefined) searchParams.append('upcoming', params.upcoming.toString());
    if (params?.organizer) searchParams.append('organizer', params.organizer);
    if (params?.enroll) searchParams.append('enroll', params.enroll);
    if (params?.city) searchParams.append('city', params.city);
    if (params?.state) searchParams.append('state', params.state);
    if (params?.location) searchParams.append('location', params.location);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return await apiGet<EventListResponse>(`${API_ENDPOINTS.EVENTS.GET_ALL}${query ? `?${query}` : ''}`);
  },

  async getMyEvents(params?: EventListParams & { isActive?: boolean; businessId?: string }): Promise<EventListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.businessId) searchParams.append('businessId', params.businessId);
    const query = searchParams.toString();
    return await apiGet<EventListResponse>(`${API_ENDPOINTS.EVENTS.MY_EVENTS}${query ? `?${query}` : ''}`);
  },

  async getEvent(id: string): Promise<EventResponse> {
    return await apiGet<EventResponse>(API_ENDPOINTS.EVENTS.GET_BY_ID(id));
  },

  async createEvent(data: CreateEventRequest): Promise<EventResponse> {
    return await apiPost<EventResponse>(API_ENDPOINTS.EVENTS.CREATE, data);
  },

  async updateEvent(id: string, data: UpdateEventRequest): Promise<EventResponse> {
    return await apiPut<EventResponse>(API_ENDPOINTS.EVENTS.UPDATE(id), data);
  },

  async deleteEvent(id: string): Promise<{ success: boolean; message: string }> {
    return await apiDelete<{ success: boolean; message: string }>(API_ENDPOINTS.EVENTS.DELETE(id));
  },

  async enrollEvent(id: string): Promise<{ success: boolean; message: string }> {
    return await apiPost<{ success: boolean; message: string }>(API_ENDPOINTS.EVENTS.ENROLL(id), {});
  },

  async markInactiveEvent(id: string): Promise<EventResponse> {
    return await apiPatch<EventResponse>(API_ENDPOINTS.EVENTS.MARK_INACTIVE(id), {});
  },

  async viewEvent(id: string): Promise<EventResponse> {
    return await apiPatch<EventResponse>(`${API_ENDPOINTS.EVENTS.GET_ALL}/${id}/view`, {}, { showLoader: false });
  },

  async getEnrolledUsers(id: string, page = 1, limit = 10): Promise<EnrolledUsersResponse> {
    return await apiGet<EnrolledUsersResponse>(
      `${API_ENDPOINTS.EVENTS.ENROLLED_USERS(id)}?page=${page}&limit=${limit}`
    );
  },

  async exportEnrolledUsers(id: string): Promise<Blob> {
    return await apiClient.getBlob(API_ENDPOINTS.EVENTS.ENROLLED_USERS_EXPORT(id));
  },

  async reportEvent(id: string, reason: string): Promise<{ success: boolean; message: string; data?: any }> {
    return await apiPost<{ success: boolean; message: string; data?: any }>(
      API_ENDPOINTS.EVENTS.REPORT(id),
      { reason }
    );
  },

  async getEventReports(page = 1, limit = 10): Promise<any> {
    return await apiGet<any>(`${API_ENDPOINTS.EVENTS.REPORTS}?page=${page}&limit=${limit}`);
  },
};
